import { useState } from "react";
import {
    TextField,
    Button,
    Card,
    CardContent,
    Alert,
    Container,
    Box,
    Typography,
    CircularProgress,
    Link as MuiLink
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import EmailIcon from "@mui/icons-material/Email";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { APP_IMAGES } from "../../constants/AppImages";
import { APP_COLORS } from "../../theme/appTheme";
import { intl } from "dmsbasereact";
import { getBaseURL } from "../../build_config";


export const ForgotPasswordPage = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState("");
    const [debugLink, setDebugLink] = useState("");

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        setSuccess(false);
        setDebugLink("");

        try {
            const response = await fetch(`${getBaseURL()}/password-recovery/request`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ email }),
            });

            const data = await response.json();

            if (response.ok) {
                setSuccess(true);

                // Check if debug link is present in response
                if (data.data && data.data.debug_reset_link) {
                    setDebugLink(data.data.debug_reset_link);
                }
            } else {
                setError(data.detail || intl.get("ErrorOccurredTryAgain").d("An error occurred. Please try again."));
            }
        } catch (err) {
            setError(intl.get("UnableToConnectServer").d("Unable to connect to server. Please try again later."));
        } finally {
            setLoading(false);
        }
    };

    const isValidEmail = (email) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    return (
        <Container
            maxWidth="false"
            disableGutters
            sx={{
                background: '#FFFFFF',
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "100vh",
                    width: "100%",
                    px: 2,
                }}
            >
                {/* Logo Section */}
                <Box
                    sx={{
                        mb: 4,
                        textAlign: "center",
                    }}
                >
                    <Box
                        component="img"
                        src={APP_IMAGES.LOGO}
                        alt="Lena Logo"
                        sx={{
                            width: "150px",
                            height: "auto",
                            mb: 2,
                            filter: "drop-shadow(2px 2px 8px rgba(0,0,0,0.3))",
                        }}
                    />
                </Box>

                <Card sx={{
                    maxWidth: "28rem",
                    width: "28rem",
                    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
                    borderRadius: 3,
                }}>
                    <CardContent>
                        <Box mb={3}>
                            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: APP_COLORS.neutral.gray900 }}>
                                {intl.get("ForgotPasswordTitle").d("Forgot Password")}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {intl.get("ForgotPasswordInstructions").d("Enter your email address and we'll send you a link to reset your password")}
                            </Typography>
                        </Box>

                        {success ? (
                            <Box>
                                <Alert severity="success" sx={{ mb: 3 }}>
                                    {intl.get("PasswordResetEmailSent").d("If an account exists with this email, you will receive a password reset link shortly. Please check your inbox and spam folder.")}
                                </Alert>

                                {debugLink && (
                                    <Alert severity="warning" sx={{ mb: 3 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                            {intl.get("ForgotPassword.DebugModeTitle").d("DEBUG MODE - Development Only")}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            {intl.get("ForgotPassword.DebugModeMessage").d("SMTP email could not be sent. Use this link to reset your password:")}
                                        </Typography>
                                        <Box
                                            sx={{
                                                p: 1,
                                                bgcolor: 'rgba(0, 0, 0, 0.05)',
                                                borderRadius: 1,
                                                fontFamily: 'monospace',
                                                fontSize: '12px',
                                                wordBreak: 'break-all',
                                                mb: 1
                                            }}
                                        >
                                            {debugLink}
                                        </Box>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => window.location.href = debugLink}
                                            sx={{ mt: 1 }}
                                        >
                                            {intl.get("ForgotPassword.OpenResetLink").d("Open Reset Link")}
                                        </Button>
                                    </Alert>
                                )}

                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={() => navigate("/login")}
                                    startIcon={<ArrowBackIcon />}
                                    sx={{
                                        py: 1.5,
                                        backgroundColor: APP_COLORS.primary.main,
                                        color: '#FFFFFF',
                                        fontWeight: 600,
                                        '&:hover': {
                                            backgroundColor: APP_COLORS.primary.dark,
                                        },
                                    }}
                                >
                                    {intl.get("BackToLogin").d("Back to Login")}
                                </Button>
                            </Box>
                        ) : (
                            <form onSubmit={handleSubmit}>
                                {error && (
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                        {error}
                                    </Alert>
                                )}

                                <Box mb={3}>
                                    <TextField
                                        required
                                        fullWidth
                                        id="email"
                                        label={intl.get("EmailAddress").d("Email Address")}
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        disabled={loading}
                                        variant="outlined"
                                        InputProps={{
                                            startAdornment: <EmailIcon sx={{ mr: 1, color: APP_COLORS.neutral.gray600 }} />,
                                        }}
                                    />
                                </Box>

                                <Box mb={2}>
                                    <Button
                                        type="submit"
                                        fullWidth
                                        variant="contained"
                                        disabled={loading || !email || !isValidEmail(email)}
                                        sx={{
                                            py: 1.5,
                                            backgroundColor: APP_COLORS.primary.main,
                                            color: '#FFFFFF',
                                            fontWeight: 600,
                                            '&:hover': {
                                                backgroundColor: APP_COLORS.primary.dark,
                                            },
                                        }}
                                    >
                                        {loading ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <CircularProgress size={20} color="inherit" />
                                                <span>{intl.get("ForgotPassword.Sending").d("Sending...")}</span>
                                            </Box>
                                        ) : (
                                            intl.get("SendResetLink").d("Send Reset Link")
                                        )}
                                    </Button>
                                </Box>

                                {loading && (
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        {intl.get("ForgotPassword.SendingEmailMessage").d("Sending password reset email... This may take a few moments.")}
                                    </Alert>
                                )}

                                <Box textAlign="center">
                                    <MuiLink
                                        component="button"
                                        type="button"
                                        variant="body2"
                                        onClick={() => navigate("/login")}
                                        sx={{
                                            textDecoration: "none",
                                            color: APP_COLORS.primary.main,
                                            fontWeight: 600,
                                            '&:hover': {
                                                textDecoration: 'underline',
                                            }
                                        }}
                                    >
                                        <ArrowBackIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: "middle" }} />
                                        {intl.get("BackToLogin").d("Back to Login")}
                                    </MuiLink>
                                </Box>
                            </form>
                        )}
                    </CardContent>
                </Card>
            </Box>
        </Container>
    );
};
